import { z } from 'zod'
import type { ItemParaRegistrar, NovaOrdemDeServico } from '../../../types/ordemDeServico'
import { paraNumero } from '../../../lib/dinheiro'

// `paraNumero` nasceu aqui e mudou para `lib/dinheiro.ts` quando o mesmo risco apareceu no valor da
// nota fiscal e nos nove campos do IIE. A reexportação mantém quem já importava daqui.
export { paraNumero }

/** Uma data sem hora, como o servidor a espera e como `dataSimples` a lê. */
const DATA = /^\d{4}-\d{2}-\d{2}$/

const obrigatorio = (mensagem: string) => z.string().trim().min(1, mensagem)

/**
 * Um valor em reais digitado à mão, com vírgula ou ponto.
 *
 * Fica como texto no formulário e vira número só na hora de enviar: um `<input type="number">`
 * apagaria o que a pessoa digitou quando ela escreve "219.444,44", e o campo em branco viraria
 * `NaN` sem aviso.
 */
const dinheiro = (mensagem: string) =>
  z
    .string()
    .trim()
    .refine((texto) => Number.isFinite(paraNumero(texto)) && paraNumero(texto) > 0, mensagem)

/**
 * Uma linha da seção 4.
 *
 * **A quantidade é uma fração, e os dois lados são inteiros.** É assim que o documento a escreve —
 * "25/30" — e é o que impede alguém guardar `0,83`, que perderia R$ 731,48 na linha de sustentação
 * de maio/2026, ou `0,8333…`, que mentiria sobre o documento. Quantidade inteira é a fração com
 * denominador 1.
 */
export const esquemaDoItem = z.object({
  item: obrigatorio('Toda linha precisa do identificador do item ("1", "2", "S2.1").'),
  descricao: obrigatorio('Descreva o serviço desta linha.'),
  metrica: z.enum(['PONTO_DE_FUNCAO', 'VALOR_FIXO'], {
    errorMap: () => ({ message: 'Escolha a unidade de medição do anexo.' }),
  }),
  unidade: z.string().trim().optional(),
  catser: z.string().trim().optional(),
  valorUnitario: dinheiro('Informe o valor unitário da linha, maior que zero.'),
  quantidadeNumerador: z.coerce.number().int().positive('O número de cima da fração precisa ser inteiro e positivo.'),
  quantidadeDenominador: z.coerce
    .number()
    .int()
    .positive('O número de baixo da fração precisa ser inteiro e positivo. Quantidade inteira usa 1.'),
  valorTotalDeclarado: dinheiro('Informe o valor total da linha como ele está no documento.'),
})

/**
 * A ordem de serviço inteira, num formulário só.
 *
 * **Não há rascunho.** O documento já chega assinado e registrar é gravar um fato — por isso o
 * formulário longo é montado no navegador e submetido de uma vez. As regras cruzadas dos dois
 * anexos ficam no `superRefine` abaixo, e o servidor as revalida: o que se ganha aqui é a pessoa
 * descobrir o que falta antes de mandar vinte e quatro campos.
 */
const camposDaOs = z.object({
  numeroExterno: z.string().trim().max(60, 'O número do documento tem no máximo 60 caracteres.').optional(),
  regime: z.enum(['SUSTENTACAO_INFRAESTRUTURA', 'PROJETO_PONTO_DE_FUNCAO'], {
    errorMap: () => ({ message: 'Escolha o modelo da ordem.' }),
  }),
  tipo: z.enum(['', 'INICIAL', 'FINAL']).default(''),
  ordemDeServicoInicial: z.string().trim().optional(),
  competencia: z.string().trim().optional(),
  periodoInicio: z.string().regex(DATA, 'Informe o início do período.'),
  periodoFim: z.string().regex(DATA, 'Informe o fim do período.'),
  contrato: obrigatorio('Informe o contrato.'),
  objeto: obrigatorio('Informe o objeto contratado.'),
  objetivo: obrigatorio('Descreva o objetivo desta ordem.'),
  requisitosNaoFuncionais: z.string().trim().optional(),
  artefatosFornecidos: z.string().trim().optional(),
  artefatosAGerar: z.string().trim().optional(),
  unidade: obrigatorio('Informe a unidade requisitante.'),
  solicitanteNome: obrigatorio('Informe quem pediu o serviço.'),
  solicitanteEmail: z.string().trim().email('Informe um e-mail válido de quem pediu.'),
  fiscalRequisitanteNome: obrigatorio('Informe o fiscal requisitante que assina a ordem.'),
  fiscalRequisitanteMatricula: obrigatorio('Informe a matrícula do fiscal requisitante.'),
  fiscalRequisitanteAssinouEm: z.string().regex(DATA, 'Informe a data em que o fiscal requisitante assinou.'),
  gestorNome: obrigatorio('Informe o gestor do contrato.'),
  gestorMatricula: obrigatorio('Informe a matrícula do gestor.'),
  gestorAssinouEm: z.string().regex(DATA, 'Informe a data em que o gestor assinou.'),
  valorTotal: dinheiro('Informe o valor total estimado do rodapé do documento.'),
  emitidaEm: z.string().regex(DATA, 'Informe a data de emissão.'),
  itens: z.array(esquemaDoItem).min(1, 'A ordem precisa de ao menos uma linha de serviço.'),
})

export const esquemaDeOs = camposDaOs.superRefine(exigirAsRegrasCruzadas)

/**
 * O formulário como o React Hook Form o guarda — a SAÍDA do esquema, com a coerção já aplicada.
 *
 * É a convenção do `esquema.ts` da GMUD (`z.infer`), e ela importa aqui: os dois lados da fração
 * chegam do campo como texto e saem como inteiros, e é o tipo de saída que impede alguém somar
 * strings mais adiante.
 */
export type FormularioDeOs = z.infer<typeof esquemaDeOs>

/**
 * As quatro regras que dependem de mais de um campo — as mesmas que o domínio aplica.
 *
 * **A competência é exigida no mensal e proibida no de projeto**, e isso não é preferência de
 * cadastro: a ordem do ANEXO IB é uma por mês (4.1.2.3.13, p.16) e sem o mês ela é inauditável; a
 * do ANEXO IA atravessa meses (ANEXO V, p.110) e uma competência ali a faria entrar na apuração do
 * mês errado. Pela mesma razão o **tipo Inicial/Final só existe no regime de projeto**, e a Final
 * aponta a Inicial que ela conclui.
 *
 * O período dentro da competência fecha o cerco: uma ordem de maio com período em junho passaria
 * pelas duas regras acima e ainda assim mediria o mês errado.
 */
function exigirAsRegrasCruzadas(dados: Campos, ctx: z.RefinementCtx) {
  const mensal = dados.regime === 'SUSTENTACAO_INFRAESTRUTURA'

  if (mensal && !/^\d{4}-\d{2}$/.test(dados.competencia ?? '')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['competencia'],
      message: 'A ordem mensal pertence a um mês de fiscalização. Informe o mês.',
    })
  }
  if (!mensal && dados.competencia) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['competencia'],
      message: 'A ordem de projeto atravessa meses e não pertence a um: deixe o mês em branco.',
    })
  }
  if (!mensal && !dados.tipo) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['tipo'],
      message: 'A ordem de projeto é Inicial ou Final (ANEXO V, Fases 1 e 2).',
    })
  }
  if (mensal && dados.tipo) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['tipo'],
      message: 'Inicial e Final só existem na ordem de projeto.',
    })
  }
  if (dados.tipo === 'FINAL' && !dados.ordemDeServicoInicial) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['ordemDeServicoInicial'],
      message: 'A ordem Final conclui uma Inicial. Informe qual.',
    })
  }
  if (mensal && dados.competencia && !dados.periodoInicio.startsWith(dados.competencia)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['periodoInicio'],
      message: 'O período da ordem mensal fica dentro do mês dela.',
    })
  }
  if (mensal && dados.competencia && !dados.periodoFim.startsWith(dados.competencia)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['periodoFim'],
      message: 'O período da ordem mensal fica dentro do mês dela.',
    })
  }
}

/** Os campos como o `superRefine` os enxerga, depois da coerção do zod. */
type Campos = z.output<typeof camposDaOs>

/**
 * O formulário validado, na forma que a API recebe.
 *
 * @param dados o formulário já validado pelo zod
 * @returns o corpo do POST
 */
export function paraRequisicao(dados: FormularioDeOs): NovaOrdemDeServico {
  const mensal = dados.regime === 'SUSTENTACAO_INFRAESTRUTURA'
  return {
    numeroExterno: dados.numeroExterno || null,
    regime: dados.regime,
    tipo: dados.tipo === '' ? null : dados.tipo,
    ordemDeServicoInicialId: dados.ordemDeServicoInicial ? Number(dados.ordemDeServicoInicial) : null,
    competencia: mensal ? (dados.competencia ?? null) : null,
    periodoInicio: dados.periodoInicio,
    periodoFim: dados.periodoFim,
    contrato: dados.contrato,
    objeto: dados.objeto,
    objetivo: dados.objetivo,
    requisitosNaoFuncionais: dados.requisitosNaoFuncionais || null,
    artefatosFornecidos: dados.artefatosFornecidos || null,
    artefatosAGerar: dados.artefatosAGerar || null,
    unidade: dados.unidade,
    solicitanteNome: dados.solicitanteNome,
    solicitanteEmail: dados.solicitanteEmail,
    fiscalRequisitanteNome: dados.fiscalRequisitanteNome,
    fiscalRequisitanteMatricula: dados.fiscalRequisitanteMatricula,
    fiscalRequisitanteAssinouEm: dados.fiscalRequisitanteAssinouEm,
    gestorNome: dados.gestorNome,
    gestorMatricula: dados.gestorMatricula,
    gestorAssinouEm: dados.gestorAssinouEm,
    valorTotal: paraNumero(dados.valorTotal),
    emitidaEm: dados.emitidaEm,
    itens: dados.itens.map(paraItem),
  }
}

/** Uma linha do formulário na forma que a API recebe. */
function paraItem(linha: FormularioDeOs['itens'][number]): ItemParaRegistrar {
  return {
    item: linha.item,
    descricao: linha.descricao,
    metrica: linha.metrica,
    unidade: linha.unidade || null,
    catser: linha.catser || null,
    valorUnitario: paraNumero(linha.valorUnitario),
    quantidadeNumerador: linha.quantidadeNumerador,
    quantidadeDenominador: linha.quantidadeDenominador,
    valorTotalDeclarado: paraNumero(linha.valorTotalDeclarado),
  }
}
